import React, { Component } from 'react';
import { Icon } from '../../components/Icon';
import { getAuthHeaders, urlMap } from '../../../utils';

export default class SummarySidePanel extends Component {
  state = {
    summary: null,
    loading: false,
    error: null,
    regenerating: false,
    editedText: '',
    saveStatus: null,
  };

  componentDidMount() {
    if (this.props.chapter?.id && this.props.projectId) {
      this.fetchSummary();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.chapter?.id !== this.props.chapter?.id ||
      prevProps.projectId !== this.props.projectId
    ) {
      if (this.props.chapter?.id && this.props.projectId) {
        this.fetchSummary();
      }
    }
  }

  fetchSummary = async () => {
    const { projectId, chapter } = this.props;

    if (!projectId || !chapter?.id) {
      this.setState({ error: 'Missing project or chapter information' });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const response = await fetch(urlMap.getProjectSummaries(projectId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const summaries = await response.json();
      const chapterSummary = Array.isArray(summaries)
        ? summaries.find((s) => s.chapter_id === chapter.id)
        : null;

      if (!chapterSummary) {
        this.setState({
          summary: null,
          loading: false,
          editedText: '',
          error: null,
        });
        return;
      }

      this.setState({
        summary: chapterSummary,
        loading: false,
        editedText: chapterSummary.summary || chapterSummary.summary_text || '',
      });
    } catch (error) {
      this.setState({
        error: error.message || 'Failed to load summary',
        loading: false,
      });
    }
  };

  handleRegenerate = async () => {
    const { chapter } = this.props;

    if (!chapter?.id) {
      alert('Cannot regenerate: missing chapter information');
      return;
    }

    this.setState({ regenerating: true, error: null });

    try {
      const response = await fetch(urlMap.regenerateChapterSummary(chapter.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const updatedSummary = await response.json();
      this.setState({
        summary: updatedSummary,
        regenerating: false,
        editedText: updatedSummary.summary || updatedSummary.summary_text || '',
      });
    } catch (error) {
      this.setState({
        error: error.message || 'Failed to regenerate summary',
        regenerating: false,
      });
      alert('Failed to regenerate summary: ' + (error.message || 'Unknown error'));
    }
  };

  handleTextChange = (e) => {
    this.setState({ editedText: e.target.value });
  };

  handleCancelEdit = () => {
    const originalText = this.state.summary?.summary || this.state.summary?.summary_text || '';
    this.setState({ editedText: originalText });
  };

  handleSaveEdit = async () => {
    const { chapter } = this.props;
    const { editedText, summary } = this.state;

    if (!chapter?.id || !summary?.id) {
      alert('Cannot save: missing required information');
      return;
    }

    this.setState({ regenerating: true, error: null });

    try {
      const response = await fetch(urlMap.updateChapterSummary(chapter.id, summary.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ summary: editedText }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const updatedSummary = await response.json();
      this.setState({
        summary: updatedSummary,
        regenerating: false,
        editedText: updatedSummary.summary || updatedSummary.summary_text || '',
        saveStatus: 'saved',
      });

      setTimeout(() => this.setState({ saveStatus: null }), 1000);
    } catch (error) {
      this.setState({
        error: error.message || 'Failed to save summary',
        regenerating: false,
        saveStatus: 'error',
      });

      setTimeout(() => this.setState({ saveStatus: null }), 1000);
      alert('Failed to save summary: ' + (error.message || 'Unknown error'));
    }
  };

  renderBody() {
    const { summary, loading, error, regenerating, editedText, saveStatus } = this.state;

    if (loading) {
      return (
        <div className="summary-modal-loading">
          <div className="summary-modal-loading-spinner"></div>
          <div>Loading summary...</div>
        </div>
      );
    }

    if (error && !summary) {
      return (
        <div className="summary-modal-error">
          <p>{error || 'Failed to load summary'}</p>
          <button
            type="button"
            className="btn primary"
            onClick={this.handleRegenerate}
            disabled={regenerating}
            style={{ marginTop: '16px' }}
          >
            {regenerating ? 'Generating...' : 'Generate Summary'}
          </button>
        </div>
      );
    }

    if (!summary) {
      return (
        <div className="summary-modal-empty">
          <p>No summary available for this chapter yet.</p>
          <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-3)' }}>
            Click the <strong>Regenerate</strong> button above to generate a summary.
          </p>
        </div>
      );
    }

    return (
      <div className="summary-modal-edit-container">
        <textarea
          className="summary-modal-edit-textarea"
          value={editedText}
          onChange={this.handleTextChange}
          placeholder="Enter summary text..."
          disabled={regenerating}
        />
        <div className="summary-modal-edit-actions">
          <button
            type="button"
            className="summary-modal-btn summary-modal-btn-secondary"
            onClick={this.handleCancelEdit}
            disabled={regenerating}
          >
            Reset
          </button>
          <button
            type="button"
            className="summary-modal-btn summary-modal-btn-primary"
            onClick={this.handleSaveEdit}
            disabled={regenerating}
          >
            {regenerating
              ? 'Saving...'
              : saveStatus === 'saved'
                ? '✓ Saved'
                : saveStatus === 'error'
                  ? '✗ Error'
                  : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { chapter, onClose } = this.props;
    const { regenerating } = this.state;

    return (
      <div className="step-results-scroll summary-side-scroll">
        <aside className="step-results-side-panel" aria-label="Chapter summary panel">
          <div className="step-results-panel-header">
            <span className="step-results-panel-title">Summary: {chapter?.title}</span>
            <div className="summary-side-actions">
              <button
                type="button"
                className={`summary-modal-reload-btn ${regenerating ? 'regenerating' : ''}`}
                onClick={this.handleRegenerate}
                disabled={regenerating}
                title="Regenerate summary"
                aria-label="Regenerate summary"
              >
                {regenerating ? (
                  <span className="summary-modal-regenerating">...</span>
                ) : (
                  <Icon.Restart />
                )}
              </button>
              <button className="btn subtle" onClick={onClose} title="Close panel">
                <Icon.Close />
              </button>
            </div>
          </div>
          <div className="step-results-panel-body summary-modal-body summary-embedded-content">
            {this.renderBody()}
          </div>
        </aside>
      </div>
    );
  }
}
